import React from 'react';
import {
    Box,
    IconButton,
    InputAdornment,
    List,
    ListItemButton,
    ListItemText,
    TextField,
    Typography
} from '@mui/material';
import { Close, Search } from '@mui/icons-material';
import { MuiModal } from 'ui/MuiModal';
import { Perk } from 'components/loyalty/models/PerkModel';
import { awardTemplateLabels } from 'components/customers/models/Award';

interface PerkSelectionModalProps {
    open: boolean;
    onClose: () => void;
    onSelect: (perk: Perk) => void;
    perks: Perk[];
}

export const PerkSelectionModal: React.FC<PerkSelectionModalProps> = ({ open, onClose, onSelect, perks }) => {
    const [searchValue, setSearchValue] = React.useState('');

    React.useEffect(() => {
        if (!open) {
            setSearchValue('');
        }
    }, [open]);

    const handleSearchChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(e.target.value);
    }, []);

    const handleClearSearch = React.useCallback(() => {
        setSearchValue('');
    }, []);

    const filteredPerks = React.useMemo(() => {
        if (!searchValue) {
            return perks;
        }
        const lower = searchValue.toLowerCase();
        return perks.filter(
            p =>
                p.title?.toLowerCase().includes(lower) ||
                p.internalTitle?.toLowerCase().includes(lower) ||
                p.token?.toLowerCase().includes(lower)
        );
    }, [perks, searchValue]);

    const handleSelect = React.useCallback(
        (perk: Perk) => () => {
            onSelect(perk);
        },
        [onSelect]
    );

    return (
        <MuiModal open={open} onClose={onClose} paperMaxWidth={500}>
            <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="h6">Select a Perk</Typography>
                    <IconButton onClick={onClose} size="small">
                        <Close />
                    </IconButton>
                </Box>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                    Choose an existing perk to use as a starting point for your template.
                </Typography>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Search perks..."
                    value={searchValue}
                    onChange={handleSearchChange}
                    size="small"
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search color="action" />
                                </InputAdornment>
                            ),
                            endAdornment: searchValue ? (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={handleClearSearch}>
                                        <Close fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : undefined
                        }
                    }}
                    sx={{ mb: 2 }}
                />
                {filteredPerks.length === 0 && (
                    <Typography variant="body2" color="textSecondary" align="center" py={4}>
                        No perks found.
                    </Typography>
                )}
                {filteredPerks.length > 0 && (
                    <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                        {filteredPerks.map(perk => {
                            const typeLabel = awardTemplateLabels[perk.template] || perk.template;
                            const subtitle = perk.internalTitle
                                ? `${typeLabel} · ${perk.internalTitle}`
                                : typeLabel;
                            return (
                                <ListItemButton key={perk._id} onClick={handleSelect(perk)}>
                                    <ListItemText primary={perk.title} secondary={subtitle} />
                                </ListItemButton>
                            );
                        })}
                    </List>
                )}
            </Box>
        </MuiModal>
    );
};
